import { useState } from 'react';
import { ShieldAlert, X } from 'lucide-react';
import { Button } from './ui/button';

const ADULT_CATEGORIES = ['cat-adult-health', 'cat-sexual-wellness'];
const AGE_VERIFIED_KEY = 'unic_age_verified';

export const isAdultCategory = (categoryId) => {
  return ADULT_CATEGORIES.includes(categoryId);
};

export const isAgeVerified = () => {
  if (typeof window === 'undefined') return false;
  const verified = localStorage.getItem(AGE_VERIFIED_KEY);
  if (!verified) return false;
  
  // Check if verification is still valid (24 hours)
  const verifiedTime = parseInt(verified, 10);
  const now = Date.now();
  const twentyFourHours = 24 * 60 * 60 * 1000;
  
  if (now - verifiedTime > twentyFourHours) {
    localStorage.removeItem(AGE_VERIFIED_KEY);
    return false;
  }
  return true;
};

export const setAgeVerified = () => {
  localStorage.setItem(AGE_VERIFIED_KEY, Date.now().toString());
};

export default function AgeVerificationModal({ isOpen, onConfirm, onCancel }) {
  const [isChecked, setIsChecked] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (isChecked) {
      setAgeVerified();
      onConfirm();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="age-verification-modal"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onCancel}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in-up">
        {/* Header */}
        <div className="bg-[#003366] p-6 text-center">
          <div className="w-16 h-16 mx-auto bg-[#FFD700]/20 rounded-full flex items-center justify-center mb-4">
            <ShieldAlert className="w-8 h-8 text-[#FFD700]" />
          </div>
          <h2 className="text-2xl font-bold text-white">Age Verification Required</h2>
          <p className="text-slate-300 mt-2 text-sm">
            This section contains adult content
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <p className="text-slate-700 text-sm leading-relaxed">
              You are attempting to access a section that contains products intended for adults only. 
              By proceeding, you confirm that you are at least <strong>18 years of age</strong> and 
              legally permitted to view such content in your jurisdiction.
            </p>
          </div>

          {/* Checkbox */}
          <label 
            className="flex items-start gap-3 cursor-pointer group mb-6"
            data-testid="age-checkbox-label"
          >
            <div className="relative flex-shrink-0 mt-0.5">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
                className="w-5 h-5 border-2 border-slate-300 rounded cursor-pointer accent-[#003366]"
                data-testid="age-checkbox"
              />
            </div>
            <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
              I confirm that I am <strong>18 years of age or older</strong> and I consent to viewing adult content.
            </span>
          </label>

          {/* Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onCancel}
              className="flex-1 border-slate-300 text-slate-600 hover:bg-slate-100"
              data-testid="age-cancel-btn"
            >
              Go Back
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!isChecked}
              className={`flex-1 ${
                isChecked 
                  ? 'bg-[#003366] hover:bg-[#002244] text-white' 
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
              data-testid="age-confirm-btn"
            >
              I'm 18+, Continue
            </Button>
          </div>

          {/* Footer note */}
          <p className="text-xs text-slate-400 text-center mt-4">
            Your verification will be remembered for 24 hours on this device.
          </p>
        </div>

        {/* Close button */}
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
          data-testid="age-close-btn"
        >
          <X size={20} />
        </button>
      </div>
    </div>
  );
}
